import { motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useWindowSize } from 'react-use'
import { useRect, useWindowOn } from 'react-use-rect'
import MarketingImage from '#app/components/marketing/image.tsx'
import { LandingSection } from '#app/components/marketing/landing-section.tsx'
import { ProductLink } from '#app/components/marketing/product-link.tsx'
import PropertiesShowcase from '#app/components/marketing/properties-showcase.tsx'
import SplitText from '#app/components/motion/split-text.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { useScroll } from '#app/hooks/use-scroll.ts'
import { usePublicContext } from '#app/routes/($lang)+/__public-context.tsx'
import { BASE_URL } from '#app/utils/config.ts'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import { LocalizedLink } from '#app/utils/i18n.tsx'
import { generateMetaTranslationLoader } from '#app/utils/i18next.server.ts'
import { clamp, mapRange } from '#app/utils/maths.ts'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { cn, getDownloadableFileSrc } from '#app/utils/misc.tsx'
import routes from '#routes/index.tsx'
import { type Route } from '../../../../.react-router/types/app/routes/($lang)+/_marketing+/+types/4d-ceramics.ts'

export const CERAMICS_4D_LANDING_PAGE_COLOR = '#000'

export async function loader({ request }: Route.LoaderArgs) {
	return {
		meta: await generateMetaTranslationLoader({
			request,
			namespace: 'fourthDimension',
			keys: {
				title: 'title',
				description: 'description',
				canonical: `${BASE_URL}${routes.public.landings.fourthDimension.route({})}`,
			},
		}),
	}
}

function TheFourthDimension() {
	const [top, setTop] = useState<number | null>(null)
	const [height, setHeight] = useState<number | null>(null)
	const [progress, setProgress] = useState(0)
	const [rectRef, revalidate] = useRect((rect) => {
		setTop(rect.top)
		setHeight(rect.height)
	})
	useWindowOn('scroll', () => revalidate())
	const { height: windowHeight } = useWindowSize()

	useScroll(({ scroll }) => {
		if (!top || !height) return

		const start = top + scroll
		const end = top + scroll + height - windowHeight

		const p1 = clamp(0, mapRange(start, end, scroll, 0, 1), 1)

		setProgress(p1)
	})

	return (
		// Wrapper
		<div
			ref={rectRef}
			className="relative h-[180vh] overflow-visible sm:h-[250vh]"
		>
			{/* Inner */}
			<div className="sticky top-0">
				<div className="relative flex h-screen items-center justify-center">
					<img
						style={{
							// Duplicate the progress of the image to make it fade in faster
							opacity: (progress - 0.1) * 2.5,
						}}
						className={cn(
							'absolute bottom-0 left-0 right-0 top-0 z-[-1] h-full w-full object-cover',
						)}
						src="/img/4D/calacatta-4d-veins.webp"
						alt="Calacatta veins 4D example"
					/>
					<h2
						className={cn(
							'z-10 m-0 mx-auto flex h-[100%] w-full items-center self-center',
							"bg-[url('/img/4D/calacatta-4d-veins.webp')] bg-cover bg-clip-text bg-center bg-no-repeat bg-origin-content",
							'hyphens-auto break-words font-serif text-[15vw] font-light uppercase leading-[100%] text-transparent',
						)}
					>
						<span className="px-sm">
							The Fourth
							<br /> Dimension
						</span>
					</h2>
					<div
						className="absolute inset-0 z-20"
						style={{
							opacity: (progress - 0.6) * 5,
						}}
					>
						<div className="relative flex h-full w-full items-center justify-center text-white">
							<video
								src={'/videos/4d-ceramics/4d-ceramics-mobile.mp4'}
								controls={false}
								loop
								autoPlay
								muted
								className="inset-0 z-0 h-screen w-full object-cover lg:hidden"
							/>
							<video
								src={'/videos/4d-ceramics/4d-ceramics-desktop.mp4'}
								controls={false}
								loop
								autoPlay
								muted
								className="inset-0 z-0 hidden h-screen w-full object-cover lg:block"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export const meta = generateTranslatedMeta

function HeroText({ className = '' }) {
	const { t } = useTranslation('fourthDimension')
	const textLines = t('what', { returnObjects: true }) as string[]

	// Subtle alignment shifts - cycles through different offsets
	const alignmentClasses = [
		'text-center pr-6', // centered
		'text-center', // slightly left of center
		'text-center pr-[100px]', // slightly right of center
		'text-center pl-12', // more left of center
		'text-center pr-4', // slightly right
		'text-center pl-6', // slightly left
	]

	return (
		<motion.div
			initial={{
				opacity: 0,
			}}
			whileInView={{
				opacity: 1,
			}}
			transition={{
				duration: 1.2,
				delay: 0.1,
				ease: 'easeInOut',
			}}
			viewport={{
				margin: '-100px 0px 0px 0px',
				amount: 0.5,
			}}
			className={`${className} mx-auto`}
		>
			<Text
				className={`inline-block select-none bg-gradient-to-r from-white to-white/10 bg-clip-text uppercase text-transparent`}
				type={'serif'}
				weight={'light'}
				as={'p'}
				size={'huge'}
			>
				{textLines.map((line, index) => (
					<div
						key={index}
						className={cn(
							'overflow-y-visible',
							alignmentClasses[index % alignmentClasses.length],
						)}
					>
						{line}
					</div>
				))}
			</Text>
		</motion.div>
	)
}

function Product({
	className = '',
	src,
	title,
}: {
	className?: string
	title: string
	src: string
}) {
	return (
		<div className={cn('flex flex-col gap-xxs lg:gap-sm', className)}>
			<MarketingImage
				className={'max-h-[450px] max-w-[225px] xl:max-h-none'}
				style={{
					aspectRatio: 1 / 2,
				}}
				src={src}
			/>
			<p
				className={
					'mx-[min(5%,16px)] break-words text-[10px] font-semibold uppercase tracking-[0.2em]'
				}
			>
				{title}
			</p>
		</div>
	)
}

export default function ComponentPage({}: Route.ComponentProps) {
	const { t } = useTranslation(['fourthDimension', 'sustainability'])
	const {
		navigation: { route },
	} = usePublicContext()

	useEffect(() => {
		route.setContent([
			{
				label: `${t('title')}.`,
			},
		])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<>
			<LandingSection
				className={'min-h-0'}
				backgroundColor={CERAMICS_4D_LANDING_PAGE_COLOR}
			>
				<HeroText className={'-mt-[18vh]'} />
			</LandingSection>

			<LandingSection
				className={'flex h-screen flex-col items-center justify-center'}
				backgroundColor={extendedTheme.colors.brand.aquamarine.DEFAULT}
			>
				<div
					className={
						'relative -translate-x-[20%] -translate-y-1/3 self-center md:-translate-x-1/4'
					}
				>
					<Text
						type={'serif'}
						weight={'light'}
						as={'h2'}
						size={'display'}
						className={'-translate-x-[5vw] select-none uppercase'}
					>
						Breccia
						<br /> Rosa
						<br /> 4D
					</Text>
					<MarketingImage
						className={'absolute left-1/3 top-1/2 max-h-[40vh] w-auto'}
						style={{
							aspectRatio: 1 / 2,
						}}
						src={'/img/4D/breccia-rosa-4d.webp'}
					/>
					<div
						className={cn(
							'h-px w-[10vw] bg-current',
							'absolute -bottom-[15vh] left-[65%]',
						)}
					/>
					{/* TODO: Place the link to the catalog of 4D Ceramics */}
					<ProductLink
						reset
						className={
							'absolute -bottom-full -right-[20vw] !border-current text-current'
						}
						link={`${routes.public.products.list.route({})}?technology=4d`}
						children={t('sustainability:to.4d.ceramics')
							.split(' ')
							.map((item: string) => (
								<span key={item}>
									{item}
									<br />
								</span>
							))}
					/>
				</div>
			</LandingSection>

			<LandingSection borderToBorderOnX>
				<div className={'flex flex-col gap-sm md:gap-md lg:gap-xl'}>
					<div
						className={
							'grid grid-cols-[repeat(16,1fr)] grid-rows-[repeat(24,7vh)] gap-sm min-[450px]:grid-rows-[repeat(26,7vh)] md:grid-rows-[repeat(22,7vh)] md:gap-md lg:grid-rows-[repeat(15,7vh)] lg:gap-xl'
						}
					>
						<div
							className={cn(
								'z-20 flex flex-col gap-sm',
								'col-start-2 -col-end-3 row-start-1',
								'md:col-end-[10]',
								'xl:col-end-8',
								'2xl:col-end-6',
							)}
						>
							<Text
								as={'h2'}
								size={'h2'}
								className={'-space-y-2 uppercase'}
								weight={'light'}
								type={'serif'}
							>
								<SplitText as={'span'}>{t('fourthDimension.title')}</SplitText>
							</Text>
							<Text as={'p'} size={'p'}>
								<SplitText as={'span'}>
									{t('fourthDimension.description')}
								</SplitText>
							</Text>
						</div>

						<MarketingImage
							imageClassName={'object-bottom'}
							className={cn(
								'max-h-[clamp(300px,40vh,500px)]',
								'col-start-4 -col-end-1 row-span-3 row-start-4',
								'md:col-start-5',
								'lg:row-start-3',
								'xl:col-start-1 xl:col-end-9 xl:row-span-4 xl:row-start-5',
							)}
							src={'/img/4D/products/1-SAPIENSTONE_4D_4_2.webp'}
						/>

						<div
							className={cn(
								'flex gap-sm',
								'col-start-2 -col-end-2 row-span-6 row-start-8',
								'min-[450px]:-col-end-4 min-[450px]:row-span-8 min-[450px]:row-start-8',
								'lg:row-start-6',
								'xl:-col-start-8 xl:-col-end-2 xl:row-start-1 xl:translate-y-0',
								'2xl:-col-start-7',
							)}
						>
							<LocalizedLink
								to={routes.public.product.route({
									slug: 'arabescato-4d',
								})}
							>
								<Product
									src={
										'/img/4D/products/2-ss-ss02-arabescato-4d-_-320160-f1- copia.jpg'
									}
									title={'Arabescato 4D'}
								/>
							</LocalizedLink>
							<LocalizedLink
								to={routes.public.product.route({
									slug: 'silver-wave-4d',
								})}
							>
								<Product
									src={
										'/img/4D/products/3-sambra_SILVER WAVE F01 160X320 copia.jpg'
									}
									title={'Silver Wave 4D'}
								/>
							</LocalizedLink>
						</div>

						<div
							className={cn(
								'z-20 flex flex-col gap-sm',
								'col-start-4 -col-end-2 row-start-[14]',
								'min-[450px]:col-start-5 min-[450px]:row-start-[16]',
								'md:-col-start-10 md:row-start-[14]',
								'lg:row-start-10',
								'xl:-col-start-9 xl:row-start-8 xl:mt-md',
								'macbook:mt-xxl',
								'2xl:-col-start-8',
							)}
						>
							<Text
								as={'h2'}
								size={'h2'}
								className={'-space-y-2 uppercase'}
								weight={'light'}
								type={'serif'}
							>
								<SplitText as={'span'}>{t('thickness.title')}</SplitText>
							</Text>
							<Text as={'p'} size={'p'}>
								<SplitText as={'span'}>{t('thickness.description')}</SplitText>
							</Text>
						</div>

						<div
							className={cn(
								'relative',
								'col-start-2 -col-end-2 grid grid-cols-subgrid grid-rows-subgrid',
								'row-span-5 row-start-[19]',
								'min-[450px]:row-span-7 min-[450px]:row-start-[20]',
								'md:row-span-5 md:row-start-[17]',
								'lg:row-span-3 lg:row-start-[12]',
								'xl:row-span-4 xl:row-start-[11]',
								'macbook:row-span-5 macbook:row-start-[11]',
								'2xl:row-span-5 2xl:row-start-10 2xl:mt-xxl',
							)}
						>
							<LocalizedLink
								className={cn(
									'col-span-7 row-span-full',
									'min-[450px]:col-span-7',
									'md:col-span-7',
									'lg:col-span-5',
									'xl:col-span-5',
									'2xl:col-span-4',
								)}
								to={routes.public.product.route({
									slug: 'calacatta-aureo-4d',
								})}
							>
								<Product
									src={
										'/img/4D/products/4-ss-ss03-calacatta_aureo-4d-_-320160-f1- copia.jpg'
									}
									title={'Calacatta Aureo 4D'}
								/>
							</LocalizedLink>
							<LocalizedLink
								className={cn(
									'col-span-8 col-start-8 row-span-full',
									'min-[450px]:col-span-7 min-[450px]:col-start-9',
									'md:col-span-7 md:col-start-9',
									'lg:col-span-5 lg:-col-start-6',
									'xl:col-span-5 xl:-col-start-6',
									'2xl:col-span-4 2xl:-col-start-5',
								)}
								to={routes.public.product.route({
									slug: 'calacatta-4d',
								})}
							>
								<Product
									src={
										'/img/4D/products/6-ss-ss02-calacatta-4d-pol-320160-f1-ssp3216549g copia.jpg'
									}
									title={'Calacatta 4D'}
								/>
							</LocalizedLink>

							<MarketingImage
								src={'/img/4D/products/5-SAPIENSTONE_4D_9.webp'}
								className={cn(
									'absolute block',
									'-top-1/4 left-1/2 max-h-[50vw] w-[50vw] !-translate-x-1/2',
									'min-[450px]:-top-[15%] min-[450px]:left-auto min-[450px]:right-0 min-[450px]:max-h-[40vw] min-[450px]:w-[30vw] min-[450px]:!translate-x-0',
									'md:bottom-0 md:left-1/2 md:top-auto md:mb-lg md:w-[24vw] md:!-translate-x-1/2',
									'lg:relative lg:col-span-3 lg:col-start-6 lg:row-start-2 lg:-row-end-1 lg:max-h-[18vw]',
									'xl:col-span-4 xl:col-start-7 xl:max-h-[17vw]',
									'macbook:max-h-[20vw]',
									'2xl:col-span-4 2xl:col-start-6 2xl:max-h-[16vw]',
								)}
							/>
						</div>
					</div>

					<div
						className={cn(
							'grid grid-cols-[repeat(16,minmax(0,1fr))]',
							'grid-rows-[repeat(2,auto)] gap-sm gap-y-xxl',
							'md:grid-rows-[repeat(6,auto)] md:gap-md',
							'lg:gap-xl',
							'xl:grid-rows-1',
							'2xl:container',
						)}
					>
						<div
							className={cn(
								'col-start-2 -col-end-2 row-start-1 row-end-2',
								'md:col-start-10 md:-col-end-2 md:row-start-1 md:row-end-3',
								'lg:col-start-9 lg:col-end-16',
								'xl:col-start-12 xl:-col-end-2 xl:row-start-1 xl:-row-end-1',
								'macbook:col-start-13 macbook:-col-end-2',
							)}
						>
							<div
								className={cn(
									'text-brand-dusk',
									'flex flex-col justify-between gap-[10vh] border-l border-brand-dusk pl-md xl:gap-sm xl:pl-lg macbook:gap-[6vh]',
								)}
							>
								<Text as={'h2'} size={'h3'} weight={'light'} type={'serif'}>
									<SplitText as={'span'}>{t('quote')}</SplitText>
								</Text>
								<MarketingImage
									imageClassName={'object-bottom'}
									className={'h-[50vw] max-h-[250px] w-[50vw] max-w-[250px]'}
									src={'/img/4D/products/9-SAPIENSTONE_4D_1.webp'}
								/>
							</div>
						</div>

						<LocalizedLink
							className={cn(
								'col-start-2 col-end-9 row-start-2',
								'min-[450px]:col-start-2 min-[450px]:col-end-10',
								'md:col-start-2 md:col-end-7 md:row-start-3 md:-row-end-1',
								'lg:col-span-4 lg:col-start-2 lg:row-start-2',
								'xl:col-start-2 xl:col-span-5 xl:row-start-1 xl:-row-end-1',
								'macbook:col-start-3 macbook:col-span-5',
							)}
							to={routes.public.product.route({
								slug: 'jatoba-brown-4d',
							})}
						>
							<Product
								src={
									'/img/4D/products/7-ss-ss02-jatoba_brown-4d-lap-320160-f1-ssp3216555g copia.jpg'
								}
								title={'Jatoba Brown 4D'}
							/>
						</LocalizedLink>
						<LocalizedLink
							className={cn(
								'col-start-9 -col-end-2 row-start-2',
								'min-[450px]:col-start-10 min-[450px]:-col-end-2',
								'md:col-start-7 md:col-end-10 md:row-start-3 md:-row-end-1',
								'lg:col-span-4 lg:col-start-6 lg:row-start-2',
								'xl:col-span-5 xl:col-start-7 xl:row-start-1 xl:-row-end-1',
								'macbook:col-start-8 macbook:col-span-5',
							)}
							to={routes.public.product.route({
								slug: 'calacatta-macchia-vecchia-4d',
							})}
						>
							<Product
								src={
									'/img/4D/products/8-ss-ss02-calacatta_macchia_vecchia-4d-pol-320160-f1-ssp3216530g copia.jpg'
								}
								title={'Calacatta Macchia Vecchia 4D'}
							/>
						</LocalizedLink>
					</div>

					<div
						className={cn(
							'relative mt-xxl grid grid-cols-[repeat(16,minmax(0,1fr))] grid-rows-[repeat(1,450px)]',
							'2xl:container',
						)}
					>
						<LocalizedLink
							className={cn(
								'col-span-7 col-start-3 row-start-1 -row-end-1',
								'min-[450px]:col-span-6',
								'lg:col-start-4',
								'xl:col-start-6',
							)}
							to={routes.public.product.route({
								slug: 'kuroca-4d',
							})}
						>
							<Product
								src={
									'/img/4D/products/10-ss-ss02-kuroka-4d-pol-320160-f1-ssp3216547g_CMYK_ajustado copia.jpg'
								}
								title={'Kuroca 4D'}
							/>
						</LocalizedLink>
						<MarketingImage
							src={'/img/4D/products/11-SAPIENSTONE_4D_8.webp'}
							className={cn(
								'absolute right-0 top-0 h-1/2 max-w-[150px]',
								'min-[450px]:relative min-[450px]:-col-start-8 min-[450px]:-col-end-1 min-[450px]:row-start-1 min-[450px]:-row-end-1 min-[450px]:h-[90%] min-[450px]:max-w-none',
								'md:h-[95%]',
								'lg:h-[93%]',
							)}
						/>
					</div>

					{/* GREEN HYDROGEN SECTION */}
					<div
						className={cn(
							'mt-xxl grid grid-cols-[repeat(16,minmax(0,1fr))] grid-rows-[repeat(10,7vh)] gap-sm gap-y-xl',
							'lg:grid-rows-[repeat(8,7vh)]',
						)}
					>
						<MarketingImage
							src={'/img/4D/products/12-SAPIENSTONE_4D_3.webp'}
							className={cn(
								'col-span-10 col-start-1 row-start-1 row-end-3',
								'md:col-end-7 md:row-end-4',
								'lg:col-span-7 lg:col-start-1',
								'macbook:col-start-2 macbook:col-end-6 macbook:row-end-5',
							)}
							imageClassName={'macbook:object-top'}
						/>

						<div
							className={cn(
								'z-20 flex flex-col gap-sm',
								'col-start-4 -col-end-2 row-start-2',
								'md:col-start-8',
								'lg:col-start-9',
								'macbook:col-start-2 macbook:col-end-9 macbook:row-start-6',
							)}
						>
							<Text
								as={'h2'}
								size={'h2'}
								className={'-space-y-2'}
								weight={'light'}
								type={'serif'}
							>
								<SplitText as={'span'}>{t('hydrogen.title')}</SplitText>
							</Text>
							<Text as={'p'} size={'p'}>
								<SplitText as={'span'}>{t('hydrogen.description')}</SplitText>
							</Text>
						</div>

						<LocalizedLink
							className={cn(
								'col-span-7 col-start-2 row-span-4 row-start-5',
								'min-[450px]:col-span-7 min-[450px]:col-start-2 min-[450px]:row-span-5 min-[450px]:row-start-5',
								'md:col-span-7 md:col-start-2',
								'lg:col-span-5 lg:col-start-2',
								'xl:col-span-3 xl:col-start-2',
								'macbook:col-span-3 macbook:-col-end-5 macbook:row-start-1',
							)}
							to={routes.public.product.route({
								slug: 'fior-di-viola-4d',
							})}
						>
							<Product
								src={'/img/4D/2-FIOR DI VIOLA 4D.webp'}
								title={'Fior di Viola 4D'}
							/>
						</LocalizedLink>
						<LocalizedLink
							className={cn(
								'col-span-7 col-start-9 row-span-4 row-start-5',
								'min-[450px]:col-span-7 min-[450px]:col-start-9 min-[450px]:row-span-5 min-[450px]:row-start-5',
								'md:col-span-7 md:col-start-9',
								'lg:col-span-5 lg:col-start-7',
								'xl:col-span-3 xl:col-start-5',
								'macbook:col-span-3 macbook:-col-end-2 macbook:row-start-1',
							)}
							to={routes.public.product.route({
								slug: 'breccia-rosa-4d',
							})}
						>
							<Product
								src={'/img/4D/breccia-rosa-4d.webp'}
								title={'Breccia Rosa 4D'}
							/>
						</LocalizedLink>

						<div
							className={cn(
								'relative col-start-8 -col-end-2 row-span-2 row-start-9',
								'min-[450px]:row-start-10',
								'lg:col-start-10 lg:row-start-8',
								'xl:col-start-11 xl:row-start-6',
								'macbook:row-start-8',
							)}
						>
							<div
								className={cn(
									'absolute hidden h-px w-[35vw] bg-current',
									'md:-left-[60%] md:bottom-[45%] md:block',
									'lg:-left-[80%]',
									'macbook:-left-[110%]',
								)}
							/>
							<ProductLink
								localized={false}
								reset
								className={cn('mx-auto border-current text-current', 'md:mx-0')}
								link={getDownloadableFileSrc('cmguih7hb000mx99gecxtc2h4')}
								children={t('actions.discoverTheCatalog')
									.split(' ')
									.map((item: string) => (
										<span key={item}>
											{item}
											<br />
										</span>
									))}
							/>
						</div>
					</div>
				</div>
			</LandingSection>

			<TheFourthDimension />

			<PropertiesShowcase
				sectionTitle={t('possibilities.title')}
				showcaseItems={[
					{
						svg: '/img/4D/possibilities/1-BORDE-RECTO-45.svg',
						title: t('possibilities.squared'),
						image: '/img/4D/possibilities/1-BORDE-RECTO-45.webp',
					},
					{
						svg: '/img/4D/possibilities/2-BORDE-CONCAVO.svg',
						title: t('possibilities.concave'),
						image: '/img/4D/possibilities/2-BORDE-CONCAVO.webp',
					},
					{
						svg: '/img/4D/possibilities/3-BORDE-CONVEXO.svg',
						title: t('possibilities.convex'),
						image: '/img/4D/possibilities/3-BORDE-CONVEXO.webp',
					},
					{
						svg: '/img/4D/possibilities/4-BORDE-RECTO-CUADRADO.svg',
						title: t('possibilities.roundedStraight'),
						image: '/img/4D/possibilities/4-BORDE-RECTO-CUADRADO.webp',
					},
					{
						svg: '/img/4D/possibilities/5-BORDE-REDONDEADO.svg',
						title: t('possibilities.rounded'),
						image: '/img/4D/possibilities/5-BORDE-REDONDEADO.webp',
					},
					{
						svg: '/img/4D/possibilities/10-BORDE TRIPLE REDONDEO.svg',
						title: t('possibilities.tripleRounded'),
						image: '/img/4D/possibilities/10-BORDE-TRIPLE-REDONDEADO.webp',
					},
				]}
			/>
		</>
	)
}
